import React from 'react';
import { motion } from 'framer-motion';
import { History, Users, BookOpen, Shield, Globe } from 'lucide-react';

const UpattiOriginPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <a href="/about" className="hover:text-white">About Mahasabha</a>
            <span>›</span>
            <span>Brahm Kshatriya Origin</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              ब्रह्मक्षत्रियोत्त्पति
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Origin of Brahm Kshatriya - Our Ancient Heritage
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Origin Story */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <History className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">ब्रह्मक्षत्रियोत्त्पति</h2>
          </div>
          
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              ब्रह्मक्षत्रियोत्त्पति पौराणिक काल में होने का वर्णन पाया गया है। परशुरामजी द्वारा धरा को क्षत्रिय विहीन कर देने के प्रयास के दौरान राजा रतन सैन ने अपनी पांच गर्भवती रानियों के साथ परम त्यागी व परोपकारी ऋषि दधीचि के आश्रम में शरण ली। उनके पांच पुत्रों ने जन्म लिया।
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              राजा किसी काम से जब बाहर निकले तो परशुरामजी के कोप से नहीं बच सके। उनकी रानियाँ भी सती हो गयी। ऋषि ने पांचों पुत्रों की शिक्षा दीक्षा की व ब्राह्मणों जैसे संस्कार दिये। लेकिन थे तो वे क्षत्रिय। इस प्रकार वे <strong className="text-primary">ब्रह्मक्षत्रिय</strong> कहलाये।
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              ऋषि ने श्री हिंगलाज माता को कुल देवी मानकर उनकी पूजा करने की आज्ञा दी। हिंगलाज मंत्र भी दिया। उन ब्रह्मक्षत्रियों ने ऋषि दधीचि को कुल गुरु माना। आज भी ऋषि दधीचि के वंशज सारस्वत ब्राह्मण हमारे कुलगुरु हैं।
            </p>
          </div>
        </motion.div>

        {/* Significance Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6 mb-12"
        >
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">घड़ी भरने की परंपरा</h3>
            <p className="text-gray-600 text-sm">
              वर्तमान काल में भी विवाह के अवसर पर घड़ी भरने के पीछे हमारे कुलगुरु सारस्वत ब्राह्मण के त्याग की घटना जुड़ी हुयी है।
            </p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">परशुराम का निर्णय</h3>
            <p className="text-gray-600 text-sm">
              राजा रतन सैन के उक्त पुत्रों का बाद में परशुरामजी से सामना होने पर वे जान तो गये कि ये क्षत्रिय हैं लेकिन ब्रह्मक्षत्रिय हो गये हैं, तो उनका संहार नहीं किया।
            </p>
          </div>
        </motion.div>

        {/* Identity Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-primary to-orange-600 rounded-2xl p-8 text-white mb-12 text-center"
        >
          <p className="text-2xl font-semibold">
            इस प्रकार हम संस्कारों से ब्राह्मण व परम्परा से क्षत्रिय हैं।
          </p>
          <p className="text-lg text-white/90 mt-2">
            इसलिए हम आपस में <strong>"खत्री ठाकरों"</strong> शब्द से अभिवादन करते हैं।
          </p>
        </motion.div>

        {/* Brahm Kshatriya or Khatri Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">ब्रह्मक्षत्रिय या खत्री</h2>
          </div>
          
          <div className="bg-blue-50 rounded-2xl p-8 border border-blue-100">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              हम ब्रह्मक्षत्रिय हैं लेकिन अपने आपको खत्री बताते रहे हैं। रिकॉर्ड में उपनाम खत्री दर्ज कराते रहे हैं। ऐसे में खत्री या ब्रह्मक्षत्रिय का आशय समझने की आवश्यकता है। क्षत्रिय से ब्रह्मक्षत्रिय होने की कथा का वर्णन मिलता है। समयांतर में स्थानीय बोली के प्रभाववश क्षत्रिय को क्षत्री, षत्री व खत्री बोलना व लिखना शुरु किया होगा।
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              इस प्रकार हैं तो हम <strong>ब्रह्मक्षत्रिय</strong> लेकिन लिखते हैं <strong>खत्री</strong>। समाज की संस्थाओं में ब्रह्मक्षत्रिय शब्द अवश्य होता है और हमारे व्यैक्तिक नामों में खत्री अधिक प्रचलन में हैं।
            </p>
          </div>
        </motion.div>

        {/* Connection with Greater Khatri Samaj */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">वृहद खत्री समाज से संबंध</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              इस अर्थ में क्या हम उस वृहद खत्री समाज में भी शामिल हैं, जिसकी जनसंख्या बारह या पंद्रह करोड़ होना बताया जा रहा है? हमारी मूल शाखाओं में <strong>मेहरा, कपूर, टंडन, चौपड़ा, कक्कड़, गंगवाल</strong> आदि शामिल हैं। और ये उपनाम वृहद खत्री समाज में भी प्रचलित हैं। क्या यह तथ्य हमारे उनसे संबंध होने का प्रमाण है?
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              हमारे बंधु को अखिल भारतीय खत्री महा सभा के अध्यक्ष पद पर आसीन होते हमने देखा। इससे पूर्व स्थानीय खत्री सभाओं में हमारे वरिष्ठ जनों की सक्रियता देखी है। ये सभी क्या संकेत देते हैं? पंजाब, उत्तर प्रदेश आदि राज्यों बस रहे वृहद खत्री समाज से हमारी क्या संबद्धता है? विचार का विषय है और समय की आवश्यकता भी।
            </p>
            <p className="text-gray-700 leading-relaxed">
              हमने हमारा दायरा सीमित कर लिया। हमने मूल शाखाओं के अंतर्गत नुखों <strong>कीरी, छूंछा, भूत, वारडे, गिराछ</strong> आदि को अपनाया है। एक ही नुख में आपस में विवाह संबंध वर्जित रखा है क्यों कि एक नुख वालों में भाइपा माना जाता है।
            </p>
          </div>
        </motion.div>

        {/* Categories Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">ब्रह्मक्षत्रियों के वर्ग</h2>
          </div>
          
          <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              ब्रह्मक्षत्रिय भारत के समस्त राज्यों में उपस्थित हैं और विदेशों मे भी। भाषा व स्थान के आधार पर ब्रह्मक्षत्रियों में वर्गीकरण भी हुआ है।
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-bold text-gray-900">मारवाड़ी ब्रह्मक्षत्रिय</h4>
                <p className="text-sm text-gray-600">मारवाड़ से संबंध रखने वाले</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-bold text-gray-900">सिंधी ब्रह्मक्षत्रिय</h4>
                <p className="text-sm text-gray-600">सिंध से संबंधित</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-bold text-gray-900">गुजराती ब्रह्मक्षत्रिय</h4>
                <p className="text-sm text-gray-600">गुजरात से संबंधित</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-bold text-gray-900">सोरठिया ब्रह्मक्षत्रिय</h4>
                <p className="text-sm text-gray-600">सौराष्ट्र से संबंधित</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-bold text-gray-900">कच्छी ब्रह्मक्षत्रिय</h4>
                <p className="text-sm text-gray-600">कच्छ से संबंधित</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-200">
                <h4 className="font-bold text-gray-900">भावसार व अन्य</h4>
                <p className="text-sm text-gray-600">अन्य वर्ग</p>
              </div>
            </div>
            
            <p className="text-gray-700 leading-relaxed">
              उपर्युक्त वर्गीकरण के अंर्तगत व्यवहार होता रहा है। लेकिन वर्तमान में यह भेद कम होता जा रहा है व अंतर्वर्गीय विवाह शुरु हुये हैं, जो <strong>शुभ संकेत</strong> हैं। अनेक मारवाड़ी परिवारों के संबध गुजराती या सिंधी परिवारों में हुये हैं। और हो भी क्यों न? हम सभी ब्रह्मक्षत्रिय ही तो हैं। मात्र भाषा व भौगोलिक स्थिति के प्रभाव से वर्गीकृत हो गये थे। यह भेद अब मिट रहा है। आवागमन व संचार क्रांति के चलते ऐसा तीव्र गति से हो रहा है। यह एक स्वाभाविक प्रकिया है। यह स्वागत योग्य है। और अब तो वृहद खत्री समाज से भी संपर्क बढ रहा है।
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default UpattiOriginPage;
